import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usersAPI, companiesAPI } from '../../services/api';
import { CreateUserDto, UpdateUserDto, User, UserRole, UserStatus, Company } from '../../types';
import { useUser } from '../UserContext';
import { toastSuccess } from '../../utils/toast';
import { useTranslation } from 'react-i18next';

const UserForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const { isAdmin } = useUser();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateUserDto>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    role: UserRole.EMPLOYEE,
    status: UserStatus.ACTIVE,
    companyId: '',
  });

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadCompanies();
    if (isEditing) {
      loadUser();
    }
  }, [id]);

  const loadCompanies = async () => {
    try {
      const data = await companiesAPI.getAll();
      setCompanies(data);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const loadUser = async () => {
    try {
      setLoading(true);
      const user = await usersAPI.getById(id!);
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        password: '', // Don't populate password for security
        phone: user.phone || '',
        role: user.role,
        status: user.status,
        companyId: user.companyId || '',
      });
    } catch (err) {
      setError(t('users.form.errors.load'));
      console.error('Error loading user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      if (isEditing) {
        // Create update data
        const updateData: UpdateUserDto = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
          status: formData.status,
          companyId: formData.companyId,
          ...(formData.password && { password: formData.password }),
        };
        
        await usersAPI.update(id!, updateData);
      } else {
        await usersAPI.create(formData);
      }
      toastSuccess(isEditing ? t('users.form.toast.updated') : t('users.form.toast.created'));
      navigate('/users');
    } catch (err: any) {
      console.error('Error saving user:', err);
      
      // Handle backend validation errors
      if (err.response?.data?.message) {
        const errorData = err.response.data;
        
        // If it's an array of validation errors
        if (Array.isArray(errorData.message)) {
          const newFieldErrors: Record<string, string> = {};
          errorData.message.forEach((errorMsg: string) => {
            // Parse validation error messages like "email must be an email"
            const fieldMatch = errorMsg.match(/^(\w+)\s+(.+)$/);
            if (fieldMatch) {
              const [, field, message] = fieldMatch;
              newFieldErrors[field] = message;
            } else {
              // If we can't parse the field, add to general error
              setError(errorMsg);
            }
          });
          setFieldErrors(newFieldErrors);
        } else {
          // Single error message
          setError(errorData.message);
        }
      } else {
        setError(t('users.form.errors.save'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const getFieldError = (fieldName: string) => {
    return fieldErrors[fieldName] || '';
  };

  const getFieldClassName = (fieldName: string) => {
    const baseClass = "mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
    return fieldErrors[fieldName] 
      ? `${baseClass} border-red-300 focus:ring-red-500 focus:border-red-500`
      : baseClass;
  };

  if (loading && isEditing) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? t('users.form.titleEdit') : t('users.form.titleCreate')}
        </h1>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">{t('users.form.firstName')} *</label>
            <input
              type="text"
              name="firstName"
              id="firstName"
              required
              value={formData.firstName}
              onChange={handleChange}
              className={getFieldClassName('firstName')}
            />
            {getFieldError('firstName') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('firstName')}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">{t('users.form.lastName')} *</label>
            <input
              type="text"
              name="lastName"
              id="lastName"
              required
              value={formData.lastName}
              onChange={handleChange}
              className={getFieldClassName('lastName')}
            />
            {getFieldError('lastName') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('lastName')}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">{t('users.form.email')} *</label>
            <input
              type="email"
              name="email"
              id="email"
              required
              value={formData.email}
              onChange={handleChange}
              className={getFieldClassName('email')}
            />
            {getFieldError('email') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('email')}</p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">{t('users.form.phone')}</label>
            <input
              type="tel"
              name="phone"
              id="phone"
              value={formData.phone}
              onChange={handleChange}
              className={getFieldClassName('phone')}
            />
            {getFieldError('phone') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('phone')}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              {t('users.form.password')} {isEditing ? `(${t('users.form.passwordKeep')})` : '*'}
            </label>
            <input
              type="password"
              name="password"
              id="password"
              required={!isEditing}
              minLength={6}
              value={formData.password}
              onChange={handleChange}
              className={getFieldClassName('password')}
            />
            {getFieldError('password') && (
              <p className="mt-1 text-sm text-red-600">{getFieldError('password')}</p>
            )}
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700">{t('users.form.role')} *</label>
            <select
              name="role"
              id="role"
              required
              value={formData.role}
              onChange={handleChange}
              className={getFieldClassName('role')}
            >
              <option value={UserRole.EMPLOYEE}>{t('users.roles.employee')}</option>
              <option value={UserRole.BRANCH_MANAGER}>{t('users.roles.branchManager')}</option>
              <option value={UserRole.OWNER}>{t('users.roles.owner')}</option>
              {isAdmin && <option value={UserRole.ADMIN}>{t('users.roles.admin')}</option>}
            </select>
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">{t('users.form.status')}</label>
            <select
              name="status"
              id="status"
              value={formData.status}
              onChange={handleChange}
              className={getFieldClassName('status')}
            >
              <option value={UserStatus.ACTIVE}>{t('users.status.active')}</option>
              <option value={UserStatus.INACTIVE}>{t('users.status.inactive')}</option>
              <option value={UserStatus.SUSPENDED}>{t('users.status.suspended')}</option>
            </select>
          </div>

          <div>
            <label htmlFor="companyId" className="block text-sm font-medium text-gray-700">{t('users.form.company')}</label>
            <select
              name="companyId"
              id="companyId"
              value={formData.companyId}
              onChange={handleChange}
              className={getFieldClassName('companyId')}
            >
              <option value="">{t('users.form.noCompany')}</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/users')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? t('common.saving') : (isEditing ? t('users.form.update') : t('users.form.create'))}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
